import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { mcpServersClient } from '@terra/shared-rest-clients-mcp-servers';
import type {
  CreateMcpIdentityDto,
  UpdateMcpIdentityDto,
} from '@terra/shared-contracts-mcp-servers';
import { getMcpApiErrorMessage } from '@terra/shared-contracts-mcp-servers';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { getAuthHeaders } from '../../utils/authHeaders';
import { buildMcpOAuthAuthorizeQuery } from '../../utils/mcpOAuthAuthorizeQuery';

export const MCP_SERVER_IDENTITIES_QUERY_KEY = 'mcp-server-identities';

const buildClientOptions = () => ({
  baseUrl: `${getCurrentGatewayUrl()}/ai`,
});

const mcpIdentitiesQueryKey = (serverId: string) =>
  [MCP_SERVER_IDENTITIES_QUERY_KEY, serverId] as const;

export function useMcpServerIdentities({ serverId }: { serverId: string }) {
  const query = useQuery({
    queryKey: mcpIdentitiesQueryKey(serverId),
    queryFn: async () => {
      const authHeaders = await getAuthHeaders();
      const response = await mcpServersClient.listMcpServerIdentities({
        params: { id: serverId },
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status === 200) {
        return response.body.identities;
      }
      throw new Error(
        getMcpApiErrorMessage(response.body, 'Failed to load MCP identities'),
      );
    },
    enabled: Boolean(serverId),
    staleTime: 30_000,
  });

  return {
    identities: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCreateMcpServerIdentity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      serverId,
      data,
    }: {
      serverId: string;
      data: CreateMcpIdentityDto;
    }) => {
      const authHeaders = await getAuthHeaders();
      const response = await mcpServersClient.createMcpServerIdentity({
        params: { id: serverId },
        body: data,
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status !== 200) {
        throw new Error(
          getMcpApiErrorMessage(response.body, 'Failed to create MCP identity'),
        );
      }
      return response.body;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: mcpIdentitiesQueryKey(variables.serverId),
      });
    },
  });
}

export function useUpdateMcpServerIdentity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      serverId,
      identityId,
      data,
    }: {
      serverId: string;
      identityId: string;
      data: UpdateMcpIdentityDto;
    }) => {
      const authHeaders = await getAuthHeaders();
      const response = await mcpServersClient.updateMcpServerIdentity({
        params: { id: serverId, identityId },
        body: data,
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status !== 200) {
        throw new Error(
          getMcpApiErrorMessage(response.body, 'Failed to update MCP identity'),
        );
      }
      return response.body;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: mcpIdentitiesQueryKey(variables.serverId),
      });
    },
  });
}

export function useDeleteMcpServerIdentity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      serverId,
      identityId,
    }: {
      serverId: string;
      identityId: string;
    }) => {
      const authHeaders = await getAuthHeaders();
      const response = await mcpServersClient.deleteMcpServerIdentity({
        params: { id: serverId, identityId },
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status !== 200) {
        throw new Error(
          getMcpApiErrorMessage(response.body, 'Failed to delete MCP identity'),
        );
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: mcpIdentitiesQueryKey(variables.serverId),
      });
    },
  });
}

export function useAuthorizeMcpServerIdentity() {
  return useMutation({
    mutationFn: async ({
      serverId,
      identityId,
    }: {
      serverId: string;
      identityId: string;
    }) => {
      const authHeaders = await getAuthHeaders();
      const response = await mcpServersClient.oauthAuthorizeIdentity({
        params: { id: serverId, identityId },
        query: await buildMcpOAuthAuthorizeQuery(),
        overrideClientOptions: buildClientOptions(),
        extraHeaders: authHeaders,
      });
      if (response.status !== 200) {
        throw new Error(
          getMcpApiErrorMessage(
            response.body,
            'Failed to authorize MCP identity',
          ),
        );
      }
      return response.body.authorizeUrl;
    },
  });
}

export function useInvalidateMcpServerIdentities() {
  const queryClient = useQueryClient();

  return useCallback(
    async ({ serverId }: { serverId: string }) => {
      await queryClient.invalidateQueries({
        queryKey: mcpIdentitiesQueryKey(serverId),
      });
    },
    [queryClient],
  );
}
